import {
  OpsAudit,
  OptroWorkStepSummary,
  WorkStepWithUser,
} from "../../../../models/audit/workStep";

export interface WorkStepGroup {
  // null key = work steps with no OpsAudit. Only the fields the grouped table
  // renders, so rows whose audit is missing from the loaded list can still be
  // shown under a placeholder header instead of being dropped.
  opsAudit: Pick<OpsAudit, "id" | "name" | "externalRef"> | null;
  workSteps: WorkStepWithUser[];
  // Optro work steps still importing into this group, shown as placeholder
  // rows ahead of the real ones until each lands.
  importingWorkSteps: OptroWorkStepSummary[];
}

// Identifies a group across renders — for collapse state and row keys. An
// Optro audit's ref, where it has one, so the group an import shows for an
// audit it hasn't created yet (id `importing:<ref>`) keeps its identity once
// the audit exists and the group takes the audit's real id.
export const groupKey = (group: WorkStepGroup): string =>
  group.opsAudit?.externalRef ?? group.opsAudit?.id ?? "__ungrouped__";

// Groups work steps by OpsAudit, preserving the audits' order and putting
// ungrouped work steps last. Audits with no matching work steps are omitted.
// Work steps whose opsAuditId is not in `opsAudits` (list still loading, or
// its fetch failed) get a placeholder group per id so they never vanish while
// the page header still counts them.
//
// Importing work steps join the audit whose externalRef matches their
// opsAuditRef — the same key the import's find-or-create resolves the audit
// by. An audit the import hasn't created yet gets its own group, named from
// the Optro listing and slotted among the loaded audits by name — the order
// /work-steps/ops-audits returns them in — so it stays put once the audit
// is created.
export const groupWorkSteps = (
  workSteps: WorkStepWithUser[],
  opsAudits: OpsAudit[],
  importingWorkSteps: OptroWorkStepSummary[] = [],
): WorkStepGroup[] => {
  const byOpsAuditId = new Map<string, WorkStepWithUser[]>();
  const ungrouped: WorkStepWithUser[] = [];
  for (const workStep of workSteps) {
    if (workStep.opsAuditId) {
      const members = byOpsAuditId.get(workStep.opsAuditId) ?? [];
      members.push(workStep);
      byOpsAuditId.set(workStep.opsAuditId, members);
    } else {
      ungrouped.push(workStep);
    }
  }

  // The import only creates an audit when the Optro item names one, so an
  // item without an audit name lands ungrouped whatever its ref says.
  const importingByAuditRef = new Map<string, OptroWorkStepSummary[]>();
  const importingUngrouped: OptroWorkStepSummary[] = [];
  for (const importing of importingWorkSteps) {
    if (importing.opsAuditRef && importing.opsAuditName) {
      const members = importingByAuditRef.get(importing.opsAuditRef) ?? [];
      members.push(importing);
      importingByAuditRef.set(importing.opsAuditRef, members);
    } else {
      importingUngrouped.push(importing);
    }
  }

  const groups: WorkStepGroup[] = [];
  for (const opsAudit of opsAudits) {
    const members = byOpsAuditId.get(opsAudit.id);
    const importing = opsAudit.externalRef
      ? importingByAuditRef.get(opsAudit.externalRef)
      : undefined;
    if (members || importing) {
      groups.push({
        opsAudit,
        workSteps: members ?? [],
        importingWorkSteps: importing ?? [],
      });
      byOpsAuditId.delete(opsAudit.id);
      if (opsAudit.externalRef) {
        importingByAuditRef.delete(opsAudit.externalRef);
      }
    }
  }
  // Named audit groups (loaded, then any importing ones slotted in below)
  // end here; "Unknown audit" and ungrouped groups follow them.
  let namedGroupsEnd = groups.length;
  for (const [externalRef, importing] of importingByAuditRef) {
    const name = importing[0].opsAuditName ?? "Unknown audit";
    const nextByName = groups
      .slice(0, namedGroupsEnd)
      .findIndex(
        (group) => (group.opsAudit?.name ?? "").localeCompare(name) > 0,
      );
    groups.splice(nextByName === -1 ? namedGroupsEnd : nextByName, 0, {
      opsAudit: { id: `importing:${externalRef}`, name, externalRef },
      workSteps: [],
      importingWorkSteps: importing,
    });
    namedGroupsEnd += 1;
  }
  for (const [id, members] of byOpsAuditId) {
    groups.push({
      opsAudit: { id, name: "Unknown audit", externalRef: null },
      workSteps: members,
      importingWorkSteps: [],
    });
  }
  if (ungrouped.length > 0 || importingUngrouped.length > 0) {
    groups.push({
      opsAudit: null,
      workSteps: ungrouped,
      importingWorkSteps: importingUngrouped,
    });
  }
  return groups;
};
